import { useTranslate } from '@refinedev/core';
import { Table } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { ContractRateKind, CycleKind } from '@/api/generated/schemas';
import type { ContractRateOut } from '@/api/generated/schemas';
import { HintLabel } from '@/components/common/HintLabel';
import { PaymentMomentTag } from './tags';

interface ContractRatesTableProps {
  rates: ContractRateOut[];
}

export const ContractRatesTable = ({ rates }: ContractRatesTableProps) => {
  const t = useTranslate();

  return (
    <Table<ContractRateOut>
      dataSource={rates}
      rowKey="id"
      size="small"
      pagination={false}
      scroll={{ x: 'max-content' }}
    >
      <Table.Column
        dataIndex="is_default"
        title={t('tenancy.contracts.fields.is_default')}
        width={80}
        align="center"
        render={(value: boolean) => (value ? <CheckOutlined /> : null)}
      />
      <Table.Column
        dataIndex="variant"
        title={t('tenancy.contracts.fields.rate_variant')}
        render={(value: string) => t(`tenancy.contracts.rate_variant.${value}`)}
      />
      <Table.Column
        dataIndex="kind"
        title={t('tenancy.contracts.fields.rate_kind')}
        render={(value: string) => t(`tenancy.contracts.rate_kind.${value}`)}
      />
      <Table.Column
        dataIndex="exclusive_period_months"
        title={t('tenancy.contracts.fields.exclusive_period_months')}
        render={(value: number, record: ContractRateOut) =>
          record.kind === ContractRateKind.exclusive ? value : '—'
        }
      />
      <Table.Column
        dataIndex="payment_moment"
        title={t('tenancy.contracts.fields.payment_moment')}
        render={(value: string) => <PaymentMomentTag moment={value} />}
      />
      <Table.Column
        dataIndex="value"
        title={t('tenancy.contracts.fields.value')}
      />
      <Table.Column
        dataIndex="min_consumption_threshold"
        title={t('tenancy.contracts.fields.min_consumption_threshold')}
      />
      <Table.Column
        dataIndex="max_consumption_threshold"
        title={t('tenancy.contracts.fields.max_consumption_threshold')}
      />
      <Table.Column
        dataIndex="cycle_kind"
        title={t('tenancy.contracts.fields.cycle_kind')}
        render={(value: string) => t(`tenancy.contracts.cycle_kind.${value}`)}
      />
      <Table.Column
        dataIndex="cycle_length_days"
        title={t('tenancy.contracts.fields.cycle_length_days')}
        render={(value: number | null, record: ContractRateOut) =>
          // A calendar cycle closes with its month, so it has no fixed length.
          record.cycle_kind === CycleKind.calendar ? (
            <HintLabel
              label={t('tenancy.contracts.cycle_length.variable')}
              hint={t('tenancy.contracts.cycle_length.variable_hint')}
            />
          ) : (
            (value ?? '—')
          )
        }
      />
      <Table.Column
        dataIndex="max_billable_cycles"
        title={t('tenancy.contracts.fields.max_billable_cycles')}
      />
    </Table>
  );
};
